import React from 'react';
import { useConfigStore } from '@stores/configStore';
import { Activity, AlertCircle } from 'lucide-react';
import type { LoggingConfig } from '../types/config';

interface LoggingConfigFormProps {}

export const LoggingConfigForm: React.FC<LoggingConfigFormProps> = () => {
  const { config, updateLogging } = useConfigStore();
  const logging = config.logging;

  const handleUpdate = (updates: Partial<LoggingConfig>) => {
    updateLogging(updates);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold">Logging & Monitoring Configuration</h3>
      </div>

      {/* Log Level and Format */}
      <div className="border-b pb-4">
        <h4 className="font-semibold text-gray-900 mb-4">Log Settings</h4>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Log Level</label>
            <select
              value={logging.level}
              onChange={(e) => handleUpdate({ level: e.target.value as any })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="debug">Debug (Most Verbose)</option>
              <option value="info">Info (Standard)</option>
              <option value="warn">Warning (Important Only)</option>
              <option value="error">Error (Critical Only)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Minimum log level to capture</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Log Format</label>
            <select
              value={logging.format}
              onChange={(e) => handleUpdate({ format: e.target.value as any })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="json">JSON (Structured)</option>
              <option value="text">Text (Human Readable)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Format for log output</p>
          </div>
        </div>
      </div>

      {/* Log Retention */}
      <div className="border-b pb-4">
        <h4 className="font-semibold text-gray-900 mb-4">Log Retention Policy</h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Retention Days</label>
            <input
              type="number"
              value={logging.retentionDays}
              onChange={(e) => handleUpdate({ retentionDays: parseInt(e.target.value) })}
              min="1"
              max="365"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Number of days to retain log files</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max File Size (MB)</label>
              <input
                type="number"
                value={logging.maxFileSize}
                onChange={(e) => handleUpdate({ maxFileSize: parseInt(e.target.value) })}
                min="1"
                max="1000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Maximum size of a single log file</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Files</label>
              <input
                type="number"
                value={logging.maxFiles}
                onChange={(e) => handleUpdate({ maxFiles: parseInt(e.target.value) })}
                min="1"
                max="100"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Maximum number of log files to keep</p>
            </div>
          </div>
        </div>
      </div>

      {/* Logging Destinations */}
      <div className="border-b pb-4">
        <h4 className="font-semibold text-gray-900 mb-4">Logging Destinations</h4>
        
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="consoleLogging"
              defaultChecked={true}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="consoleLogging" className="text-sm font-medium text-gray-700">
              Console Logging
            </label>
          </div>
          <p className="text-xs text-gray-500 ml-6">Output logs to console/stdout</p>

          <div className="flex items-center gap-2 mt-4">
            <input
              type="checkbox"
              id="fileLogging"
              defaultChecked={true}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="fileLogging" className="text-sm font-medium text-gray-700">
              File Logging
            </label>
          </div>
          <p className="text-xs text-gray-500 ml-6">Write logs to files on disk</p>
        </div>
      </div>

      {/* Request/Response Logging */}
      <div className="border-b pb-4">
        <h4 className="font-semibold text-gray-900 mb-4">Request/Response Logging</h4>
        
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="requestResponseLogging"
              defaultChecked={true}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="requestResponseLogging" className="text-sm font-medium text-gray-700">
              Log All API Requests and Responses
            </label>
          </div>
          <p className="text-xs text-gray-500 ml-6">
            Logs HTTP method, URL, status code, response time, and payload size
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
            <div className="flex gap-2">
              <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> Enabling request/response logging may impact performance and increase disk usage. Sensitive data (passwords, tokens) will be masked.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Error Logging */}
      <div className="border-b pb-4">
        <h4 className="font-semibold text-gray-900 mb-4">Error Logging</h4>
        
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="validationErrorLogging"
              defaultChecked={true}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="validationErrorLogging" className="text-sm font-medium text-gray-700">
              Log Validation Errors
            </label>
          </div>
          <p className="text-xs text-gray-500 ml-6">
            Log errors from data validation (invoice format, field validation, etc.)
          </p>

          <div className="flex items-center gap-2 mt-4">
            <input
              type="checkbox"
              id="ftaErrorLogging"
              defaultChecked={true}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="ftaErrorLogging" className="text-sm font-medium text-gray-700">
              Log FTA/AIS Errors
            </label>
          </div>
          <p className="text-xs text-gray-500 ml-6">
            Log errors from FTA compliance checks and AIS API responses
          </p>

          <div className="flex items-center gap-2 mt-4">
            <input
              type="checkbox"
              id="databaseErrorLogging"
              defaultChecked={true}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="databaseErrorLogging" className="text-sm font-medium text-gray-700">
              Log Database Errors
            </label>
          </div>
          <p className="text-xs text-gray-500 ml-6">
            Log errors from database connections and queries
          </p>
        </div>
      </div>

      {/* Performance Monitoring */}
      <div className="border-b pb-4">
        <h4 className="font-semibold text-gray-900 mb-4">Performance Monitoring</h4>
        
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="performanceLogging"
              defaultChecked={true}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="performanceLogging" className="text-sm font-medium text-gray-700">
              Log Performance Metrics
            </label>
          </div>
          <p className="text-xs text-gray-500 ml-6">
            Log response times, database query times, and API call durations
          </p>

          <div className="bg-gray-50 rounded-lg p-3 mt-3">
            <p className="text-xs text-gray-600">
              <strong>Metrics Tracked:</strong>
            </p>
            <ul className="text-xs text-gray-600 mt-2 space-y-1 ml-4">
              <li>• API endpoint response times</li>
              <li>• Database query execution times</li>
              <li>• Invoice processing duration</li>
              <li>• AIS API call latency</li>
              <li>• Memory and CPU usage</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Log Aggregation */}
      <div>
        <h4 className="font-semibold text-gray-900 mb-4">Log Aggregation (Optional)</h4>
        
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="logAggregation"
              className="w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="logAggregation" className="text-sm font-medium text-gray-700">
              Enable Log Aggregation
            </label>
          </div>
          <p className="text-xs text-gray-500 ml-6">
            Send logs to external aggregation service (e.g., ELK Stack, Splunk, CloudWatch)
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Aggregation Endpoint (Optional)</label>
            <input
              type="url"
              placeholder="https://logs.example.com/api/logs"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">URL of log aggregation service</p>
          </div>
        </div>
      </div>
    </div>
  );
};
